import type { ReactNode } from 'react';
import { cn } from '@attendance/ui/utils';

/* ===========================================================================
 * PageHeader — the title block every screen opens with.
 *
 * Ported to the sister app's anatomy (frontend/src/components/ui/page-header.jsx):
 * a min-width-0 text column holding a truncating 20/700 title and a 14px muted
 * description, an optional hairline under the block, and a shrink-0 action
 * cluster on the trailing edge. The type lives in `.page-title` /
 * `.page-subtitle`
 * (apps/web/src/index.css) so a screen can reproduce the header ANYWHERE — inside
 * a drawer, a wizard step, a modal — without importing this component.
 *
 * TRUST THE CODE, NOT THE DOC. Their DESIGN_SYSTEM.md documents a 28px H1; the
 * `.page-title` they actually ship is `text-xl` — 20px. We follow what renders.
 * (Our `--text-h1` = 28px is the token that was derived from that doc. It stays
 * as-is — other things read it — but the page title is no longer one of them.)
 *
 * API is ADDITIVE: `title` / `description` / `actions` keep their meaning for
 * all 45 screens already consuming this; `border` and `className` are new and
 * optional. There was a `subtitle` alias for `description` — the sister app's
 * word for the same slot — and it is gone: two names for one slot is how a
 * header ends up with two spellings and no rule.
 *
 * RTL: no physical direction anywhere. `ms-auto` + `gap` + `justify-end`
 * resolve along the inline axis, so the actions sit on the trailing edge in
 * both directions, and `truncate` puts its ellipsis at the logical end.
 * `.page-title` carries a roomier line box than the console's default so
 * `truncate`'s `overflow:hidden` cannot clip Arabic diacritics — see the note
 * in index.css.
 *
 * ---------------------------------------------------------------------------
 * WHY THE HEADER WRAPS, AND WHY THE ACTION CLUSTER NO LONGER SAYS `shrink-0`
 *
 * The cluster used to be `flex shrink-0 items-center gap-element`. `flex-basis:auto`
 * + `flex-shrink:0` sizes a flex item to its children's MAX-CONTENT width — and
 * the max-content width of a `flex-wrap` container is the sum of its items
 * UNWRAPPED. So the two screens that ask for a wrapping cluster in `actions`
 * (ReportsWorkspace, WorksiteDetailPage) got a box that could never wrap: it
 * overflowed instead. Worse, since the title column is the only shrinkable item
 * (`min-w-0`), it absorbed the entire squeeze — on a narrow viewport the page
 * TITLE truncated to nothing while three buttons kept their full width. The
 * heading of the page lost to a secondary control.
 *
 * Two changes, and they only take effect when the room runs out:
 *
 *  1. The header itself wraps, and the text column is `flex-1 basis-0`. Line
 *     breaking runs BEFORE flexing and uses each item's hypothetical size, so a
 *     `basis-0` column contributes NOTHING to the break decision: a long title
 *     can never push the actions onto a second line by itself. Only a cluster
 *     genuinely too wide for the header does that — and when it does, the title
 *     gets the whole first line instead of being ground to zero. `ms-auto`
 *     (not `justify-between`) keeps the cluster on the trailing edge whether it
 *     shares the title's line or has one of its own.
 *
 *  2. The cluster wraps internally and is allowed to shrink, so a consumer's
 *     own `flex-wrap` row can finally do what it says. Note the DELIBERATE
 *     absence of `min-w-0` here: without it the cluster's automatic minimum is
 *     its min-content width, i.e. its widest single button — it will re-flow
 *     onto more lines but never squash a control into illegibility.
 *
 * DESKTOP IS UNTOUCHED. With room to spare there is no wrapping and no
 * shrinking: the text column grows into the slack exactly as `justify-between`
 * used to place it, and the cluster sits on the trailing edge of the title's
 * line. The change is only visible at the width where the old header was
 * already broken.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS NOT HERE: THE LINE ABOVE THE TITLE
 *
 * This component used to own an `eyebrow` — a dot-joined "where and when" line
 * above the title — and `PageShell` owned a `breadcrumb` slot above that. Both
 * are gone. Between them they gave nine screens a FOUR-line opening (trail,
 * crumb line, title, description) whose top two lines only restated the
 * navigation rail already on screen: "Admin panel · Administration".
 *
 * A header is a title and one supporting line. Anything that was a FACT rather
 * than a signpost — a period, a scope, an "as of" time — moves into
 * `description`, where it reads as a sentence instead of a fragment.
 * ======================================================================== */

/**
 * Exported so `PageShell` can FLATTEN this contract onto its own props rather
 * than restate it. The delegation is then total: a prop added here is a
 * `PageShell` prop with nothing to update over there, and the two can never
 * describe the header band differently.
 */
export interface PageHeaderProps {
  title: ReactNode;
  /* THERE IS NO `eyebrow`, AND NO `breadcrumb`, DELIBERATELY.
   *
   * A page header is a TITLE AND ONE SUPPORTING LINE. Nothing else. The slots
   * that used to sit above the title are gone from the type, not merely unused,
   * because a slot that exists gets filled: thirty-four screens carried an
   * eyebrow and twelve also drew a breadcrumb, so nine of them opened with FOUR
   * stacked lines — a trail, a dot-joined crumb line, the title, the
   * description — of which the first two only ever restated the rail the reader
   * was already looking at ("Admin panel · Administration").
   *
   * Deleting the prop is what makes the rule hold. Leaving it and asking people
   * not to use it is how it came back the first time; now a screen that reaches
   * for one fails to compile.
   *
   * IF SOMETHING ABOVE THE TITLE CARRIED A FACT — a period, a scope, an "as
   * of" time — that fact does not get dropped with the line. It belongs in
   * `description`, where it is a sentence the reader can act on rather than a
   * fragment in a crumb trail. See OperationsPage, whose "Live · 14:32" was the
   * only freshness statement on the screen and is now part of its subtitle. */
  /**
   * Supporting line under the title.
   *
   * ONE NAME. `subtitle` was an alias kept for the sister app's vocabulary, and
   * two names for one slot is how a header ends up with two spellings and no
   * rule — forty screens said `description` and two said `subtitle`, which is
   * not a convention, it is a coin toss nobody remembers making. The alias is
   * gone and the two callers moved.
   */
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * Keep the `h1` for the document outline and screen readers, but do not DRAW
   * it. The one legitimate case, and it is narrow enough to name: a screen the
   * rail row you just clicked has already titled, whose header would otherwise
   * be a single word above its own figures.
   *
   * IT IS NOT A WAY TO SAVE VERTICAL SPACE, and the `h1` does not go away — a
   * page with no level-1 heading is a page a screen-reader user cannot orient
   * in, and the title is still what the browser's own outline and the
   * accessibility tree read. What is removed is 34px of ink stating a word that
   * is lit in the navigation two inches to the left.
   *
   * A hidden title and a `description` do not go together: a supporting line
   * under nothing is a sentence with no subject. Pass one or the other.
   */
  titleHidden?: boolean;
  /** Close the block with a hairline, for screens that open straight into a
   *  dense table with no card between the header and the data. */
  border?: boolean;
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  titleHidden = false,
  border = false,
  className,
}: PageHeaderProps): JSX.Element {
  /* The area this screen belongs to, resolved from the route rather than
     passed in: a screen should not have to declare where it lives, and one
     that forgot would be the only page with the wrong accent. */
  return (
    <div
      className={cn(
        'mb-5 flex flex-wrap items-start gap-component',
        /* With the title hidden and no actions there is nothing in this block
           to space away from the content: the margin would be a gap under an
           empty row. */
        titleHidden && !actions && 'mb-0',
        border && 'border-b border-border pb-4',
        className
      )}
    >
      {/* `min-w-0` is what makes `truncate` work at all: without it a flex item
       *  refuses to shrink below its content's intrinsic width, so a long title
       *  pushes the actions off the edge instead of ellipsizing. `flex-1
       *  basis-0` is what keeps the title out of the wrap decision — see the
       *  header note above. */}
      <div className="min-w-0 flex-1 basis-0">
        <h1
          /* NO PILLAR MARK HERE (owner, 2026-09-04). A 3px leading edge on the
             h1 was the first home for the area accent, and it was the wrong
             one: the mark the owner asked for is a STATUS marker on a person's
             row, and putting the same shape on a page title made two unrelated
             things look like one. The rail's active marker keeps the accent —
             it is where "which area am I in" is actually asked — and the pillar
             colour still reaches the rail icons through `pillars.ts`. */
          className={
            titleHidden
              ? 'sr-only'
              : 'page-title truncate'
          }
          // A truncated title is unreadable without this; only a plain string
          // can carry it, and an empty attribute would be worse than none.
          title={typeof title === 'string' ? title : undefined}
        >
          {title}
        </h1>
        {description && <p className="page-subtitle">{description}</p>}
      </div>
      {actions && (
        /* The hook `headerCluster.test.tsx` reads the row back through, mirroring
           `data-control-bar-slot` on the toolbar one band below. A cluster whose
           members disagree about their height is invisible to every author —
           each component is correct about its own usual context — so it is
           checked rather than reviewed. */
        <div
          data-page-header-actions=""
          className="ms-auto flex flex-wrap items-center justify-end gap-element"
        >
          {actions}
        </div>
      )}
    </div>
  );
}
